import React, { useState } from "react";
import { FileText, Download, Lock, CheckCircle2, ArrowRight, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { PageHero } from "@/components/shared/PageHero";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { Reveal, RevealStagger, RevealItem } from "@/components/shared/Reveal";
import { CTABand } from "@/components/shared/CTABand";
import { toast } from "sonner";
import { RESOURCES, COMPANY } from "@/data/content";
import { submitLead, isBusinessEmail } from "@/lib/leads";

const triggerDownload = (resource) => {
  const content = `${COMPANY.name}\n${resource.type.toUpperCase()} — ${resource.title}\n${"=".repeat(48)}\n\n${resource.desc}\n\n[This is a sample document generated for the prototype.]\n\n${COMPANY.address}\n${COMPANY.phone}\n`;
  const blob = new Blob([content], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${resource.slug}.txt`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export default function Resources() {
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", email: "" });
  const [errors, setErrors] = useState({});
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formStartedAt, setFormStartedAt] = useState(() => Date.now());
  const [website, setWebsite] = useState("");

  const openGate = (resource) => {
    setSelected(resource);
    setForm({ name: "", email: "" });
    setErrors({});
    setDone(false);
    setSubmitting(false);
    setFormStartedAt(Date.now());
    setWebsite("");
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (form.name.trim().length < 2) errs.name = "Please enter your name.";
    if (!isBusinessEmail(form.email)) errs.email = "Please enter a valid business email.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSubmitting(true);
    try {
      await submitLead("resource_download", {
        ...form,
        resource: selected.slug,
        title: selected.title,
        website,
        _formStartedAt: formStartedAt,
      });
      triggerDownload(selected);
      setDone(true);
      toast.success("Download started", { description: `${selected.title} is on its way.` });
    } catch (error) {
      toast.error("We couldn't unlock the download.", { description: "Please try again or email us directly." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHero
        kicker="Resource Hub"
        title="The tools we use — free for you to steal."
        subtitle="Checklists, worksheets, and models built from thousands of real evaluations. Grab any of them with a single email — no sales call attached."
      />

      <section className="section-x py-16 sm:py-24">
        <div className="container-tmt">
          <SectionHeading
            kicker="The Library"
            title="Practical, provider-agnostic, ready to use"
            subtitle="Every asset is the real thing — the same frameworks our advisors run in live engagements."
          />
          <RevealStagger className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {RESOURCES.map((r) => (
              <RevealItem key={r.slug}>
                <div className="card-hairline group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card p-7 transition-transform duration-300 hover:-translate-y-1 hover:border-primary/40">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-primary">
                      <FileText className="h-6 w-6" />
                    </span>
                    <Badge variant="outline" className="border-primary/25 bg-primary/5 font-mono text-[11px] text-primary">
                      {r.type}
                    </Badge>
                  </div>
                  <h3 className="mt-5 font-display text-lg font-semibold leading-snug">{r.title}</h3>
                  <p className="mt-2 flex-1 text-sm text-muted-foreground">{r.desc}</p>
                  <div className="mt-5 flex items-center justify-between border-t border-border pt-4">
                    <span className="font-mono text-xs text-muted-foreground">{r.pages}</span>
                    <button
                      onClick={() => openGate(r)}
                      className="inline-flex items-center gap-1.5 font-mono text-xs text-primary transition-colors hover:text-primary-glow"
                    >
                      <Lock className="h-3.5 w-3.5" /> Get it
                    </button>
                  </div>
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* Email gate dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="sm:max-w-md">
          {!done ? (
            <>
              <DialogHeader>
                <div className="mb-2 inline-flex w-fit items-center gap-2 rounded-full border border-primary/25 bg-primary/10 px-3 py-1 font-mono text-xs text-primary">
                  <Download className="h-3.5 w-3.5" /> {selected?.type}
                </div>
                <DialogTitle className="font-display text-xl leading-snug">{selected?.title}</DialogTitle>
                <DialogDescription>Enter your details and we’ll unlock the download instantly.</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="mt-2 space-y-4">
                <div className="sr-only" aria-hidden="true">
                  <Label htmlFor="resource-website">Website</Label>
                  <Input
                    id="resource-website"
                    name="website"
                    value={website}
                    onChange={(event) => setWebsite(event.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm text-foreground/90">Full name</Label>
                  <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Jane Rivera" />
                  {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm text-foreground/90">Business email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="jane@company.com" />
                  {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                </div>
                <Button type="submit" variant="hero" size="lg" className="w-full" disabled={submitting}>
                  {submitting ? "Unlocking..." : "Download Now"} <ArrowRight className="h-4 w-4" />
                </Button>
                <p className="flex items-center justify-center gap-1.5 text-center font-mono text-xs text-muted-foreground">
                  <ShieldCheck className="h-3.5 w-3.5 text-primary" /> No spam. No sales call. Just the file.
                </p>
              </form>
            </>
          ) : (
            <div className="py-6 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-success/15">
                <CheckCircle2 className="h-8 w-8 text-success" />
              </div>
              <h3 className="mt-5 font-display text-2xl font-semibold">Your download is ready</h3>
              <p className="mx-auto mt-2 max-w-sm text-muted-foreground">
                {selected?.title} just downloaded. Check your downloads folder — and reach out if you’d like us to walk you through it.
              </p>
              <Button variant="outline" className="mt-6" onClick={() => setSelected(null)}>Browse more resources</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CTABand title="Want the frameworks applied to your actual stack?" subtitle="Book a 25-minute consultation and we'll run them against your real contracts — no pitch." />
    </>
  );
}
